// Service Steady PWA — src/lib/weatherDayparts.ts — built 2026-06-05
//
// Pure aggregation of hourly precip into a 4-daypart breakdown
// (Morning / Afternoon / Evening / Overnight), shared by WeatherCard (compact
// glance) and WeatherDetailModal (full row). No React, no fetch — just data
// in, dayparts out, so it's trivially testable and reused verbatim by both
// surfaces.
//
// Boundaries use the LOCAL hour parsed straight from each hourly entry's
// `time`, which the Worker returns as location-local ISO (e.g.
// "2026-06-05T14:00"). That means daypart boundaries are correct for the
// LOCATION, not the device — no timezone conversion needed.

import type { WeatherHourly } from './api/weather';

export type DaypartKey = 'morning' | 'afternoon' | 'evening' | 'overnight';

export interface DaypartDef {
  key: DaypartKey;
  /** Full label for the modal. */
  label: string;
  /** Compact label for the card glance. */
  short: string;
  /** Inclusive start hour (local, 0–23). */
  startHour: number;
  /** Exclusive end hour (local; 24 = midnight). */
  endHour: number;
}

// Daypart windows — tweak boundaries here. Display order is the runbook order
// (Morning → Afternoon → Evening → Overnight), i.e. forward through the day
// with Overnight read as the COMING night (see daypartsFromDetail).
export const DAYPARTS: readonly DaypartDef[] = [
  { key: 'morning', label: 'Morning', short: 'Morn', startHour: 6, endHour: 12 },
  { key: 'afternoon', label: 'Afternoon', short: 'Aft', startHour: 12, endHour: 18 },
  { key: 'evening', label: 'Evening', short: 'Eve', startHour: 18, endHour: 24 },
  { key: 'overnight', label: 'Overnight', short: 'Night', startHour: 0, endHour: 6 },
] as const;

export interface DaypartPrecip extends DaypartDef {
  /**
   * Chance of rain for the window. Aggregation = PEAK hour (the max
   * precip_chance_pct across the window's hours) — the most intuitive answer
   * to "can I detail during that block?" (plan around the worst hour).
   *
   * Statistically-honest alternative — P(≥1 rainy hour) = 1 − ∏(1 − pᵢ/100)
   * — is left in a comment in computeDayparts() for an easy swap if Ernie
   * prefers it.
   *
   * null when no hourly data falls in the window (e.g. a forward Overnight
   * whose hours aren't in the payload yet, or an elapsed block dropped from a
   * rolling hourly array).
   */
  peakPct: number | null;
  hasData: boolean;
  /** The block containing the location's current local hour. */
  isCurrent: boolean;
  /**
   * The block's window has fully passed earlier TODAY (Morning/Afternoon/
   * Evening only). Overnight is forward-looking and is never elapsed.
   */
  isElapsed: boolean;
  /** True when this block's data is sourced from tomorrow (forward Overnight). */
  isTomorrow: boolean;
}

/** Parse the local hour (0–23) out of a "…THH:…" ISO string. */
function hourOf(iso: string | undefined): number | null {
  if (!iso) return null;
  const m = /T(\d{2}):/.exec(iso);
  if (!m || !m[1]) return null;
  const h = parseInt(m[1], 10);
  return Number.isFinite(h) ? h : null;
}

function clampPct(p: number): number {
  if (!Number.isFinite(p)) return 0;
  return Math.max(0, Math.min(100, Math.round(p)));
}

/** Which daypart contains a given local hour. */
export function daypartForHour(hour: number): DaypartKey {
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 24) return 'evening';
  return 'overnight'; // 0–6
}

function peakOver(hours: readonly WeatherHourly[], startHour: number, endHour: number): number | null {
  let peak: number | null = null;
  // 1−∏ alternative:
  //   let product = 1; for (...) product *= (1 - clampPct(h.precip_chance_pct) / 100);
  //   return everyHour ? Math.round((1 - product) * 100) : null;
  for (const h of hours) {
    const hr = hourOf(h.time);
    if (hr === null || hr < startHour || hr >= endHour) continue;
    const p = clampPct(h.precip_chance_pct);
    peak = peak === null ? p : Math.max(peak, p);
  }
  return peak;
}

/**
 * Build the 4-daypart precip breakdown.
 *
 * Morning/Afternoon/Evening read from today's hours. Overnight is
 * forward-looking: while we're still IN the overnight (nowHour < 6) it reads
 * today's 0–6; otherwise it refers to the COMING night and reads tomorrow's
 * 0–6. This keeps the row planning-oriented ("rain this morning / this
 * afternoon / tonight / overnight") rather than echoing hours already gone.
 */
export function computeDayparts(
  todayHours: readonly WeatherHourly[],
  tomorrowHours: readonly WeatherHourly[],
  nowHour: number,
): DaypartPrecip[] {
  const currentKey = daypartForHour(nowHour);
  return DAYPARTS.map((d) => {
    const useTomorrow = d.key === 'overnight' && nowHour >= 6;
    const src = useTomorrow ? tomorrowHours : todayHours;
    const peakPct = peakOver(src, d.startHour, d.endHour);
    // Elapsed only applies to the daytime blocks that have fully passed today.
    // Overnight is always the upcoming night, so it's never elapsed.
    const isElapsed = d.key !== 'overnight' && d.endHour <= nowHour;
    return {
      ...d,
      peakPct,
      hasData: peakPct !== null,
      isCurrent: d.key === currentKey,
      isElapsed,
      isTomorrow: useTomorrow,
    };
  });
}

/** Minimal shape of the detail payload's ok branch that we need. */
export interface DaypartDetailInput {
  hourly_today: WeatherHourly[];
  hourly_by_day: Record<string, WeatherHourly[]>;
  daily: { date: string; day_name: string }[];
}

/**
 * Convenience: derive dayparts straight from the (ok) weather detail payload.
 * Resolves the location-local "now" hour from hourly_today[0] (the Worker
 * marks it "Now"), the full-day hours from hourly_by_day[todayKey] (falling
 * back to the rolling hourly_today), and tomorrow's hours for forward
 * Overnight.
 */
export function daypartsFromDetail(d: DaypartDetailInput): DaypartPrecip[] {
  const nowAnchor = d.hourly_today[0]?.time;
  const nowHour = hourOf(nowAnchor) ?? new Date().getHours();

  const todayKey =
    d.daily.find((x) => x.day_name === 'Today')?.date ?? nowAnchor?.slice(0, 10);
  const tomorrowKey =
    d.daily.find((x) => x.day_name === 'Tomorrow')?.date ?? d.daily[1]?.date;

  const todayHours =
    todayKey && d.hourly_by_day[todayKey]?.length
      ? d.hourly_by_day[todayKey]
      : d.hourly_today;
  const tomorrowHours = (tomorrowKey && d.hourly_by_day[tomorrowKey]) || [];

  return computeDayparts(todayHours, tomorrowHours, nowHour);
}
